import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, PackageSearch, Box } from "lucide-react";
import { api, apiError } from "../lib/api";
import { TrackingTimeline } from "../components/TrackingTimeline";

const STATUS_LABEL = {
  PLACED: "Pesanan Dibuat", PURCHASED: "Dibeli dari Supplier", GUANGZHOU_HUB: "Tiba di Gudang China",
  CUSTOMS: "Proses Bea Cukai", INDO_WAREHOUSE: "Tiba di Gudang Indonesia", DISPATCHED_KEDAI: "Dikirim ke Kedai",
  READY_PICKUP: "Siap Diambil", COMPLETED: "Selesai",
};

export default function Track() {
  const [params] = useSearchParams();
  const [awb, setAwb] = useState((params.get("awb") || "").toUpperCase());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = async (val) => {
    const code = (val ?? awb).trim();
    if (!code) return;
    setLoading(true);
    setData(null);
    try {
      const r = await api.get(`/track/${encodeURIComponent(code)}`);
      setData(r.data);
      const done = (r.data.tracking || []).filter((x) => x.done);
      const cur = done[done.length - 1];
      if (cur) {
        const hasPhoto = cur.photos && cur.photos.length > 0;
        toast.info(`Status terkini: ${cur.label}`, { description: hasPhoto ? "Foto barang tersedia — lihat di bawah." : `AWB ${r.data.awb}` });
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = params.get("awb");
    if (initial) doSearch(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="track-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><PackageSearch size={28} /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900">Lacak Pesanan</h1>
        <p className="text-stone-500 mt-2 text-sm">Masukkan nomor AWB pesananmu untuk melihat posisi barang & foto saat tiba di gudang. Nomor AWB ada di halaman Pesanan / struk (contoh: JST9B3AD704D).</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); doSearch(); }} className="flex flex-col sm:flex-row gap-3 mb-8">
        <input data-testid="track-awb-input" value={awb} onChange={(e) => setAwb(e.target.value.toUpperCase())} placeholder="Masukkan nomor AWB (mis. JST9B3AD704D)"
          className="flex-1 border border-stone-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-500 font-mono tracking-wide" />
        <button data-testid="track-submit" type="submit" disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-7 py-3 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <PackageSearch size={18} />}Lacak
        </button>
      </form>

      {data && (
        <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-5 border-b border-stone-100">
            <div>
              <p className="font-mono text-xs text-stone-400">AWB: {data.awb}</p>
              <p className="text-sm font-medium text-stone-800 flex items-center gap-1.5 mt-1"><Box size={15} className="text-stone-400" />{data.items.map((it) => `${it.title} ×${it.qty}`).join(", ")}</p>
            </div>
            <span className="text-sm font-bold px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="track-status">{STATUS_LABEL[data.status] || data.status}</span>
          </div>
          <h2 className="font-display font-semibold text-lg mb-4">Pelacakan Kiriman (China → Indonesia)</h2>
          <TrackingTimeline tracking={data.tracking} resi={{ GUANGZHOU_HUB: data.resi_china, INDO_WAREHOUSE: data.resi_jakarta }} photoSrc={(p) => `${process.env.REACT_APP_BACKEND_URL}/api/track/${encodeURIComponent(data.awb)}/photo?path=${encodeURIComponent(p)}`} />
        </div>
      )}
    </div>
  );
}
